/**
 * 案件の並べ替えの下書きを画面で組み立てる決まり（スプシは変えない）。
 *
 * 並べる区分（置き場所が決まっていない案件と、並べる4つの区分）の札の並びを「並び（Arrangement）」として持ち、
 * 札を動かすたびに新しい並びを作る。並びと一覧の元の並びの差から、API に保存する下書きの区分・下の帯の
 * 「未反映の並べ替え n件」・札の「動かした 12→3」を決める。動かせる先は API と同じ表（`canMoveCard`）で決め、
 * 動かした札の数え方も API と同じ関数（`movedCards`）を使う。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の札を区分の中・区分の間・未振り分けから動かした並びを組み立て、保存する下書き・未反映の並べ替えの一覧・札の印を、API と同じ動かせる先の表と数え方で決める
 * @graph-connects none
 */
import {
  canMoveCard,
  movedCards,
  type ProductBacklogCard,
  type ProductBacklogDraft,
  type ProductBacklogDraftSection,
  type ProductBacklogSection,
  type ProductBacklogSectionKind,
} from '@cortex/domain-ac-mens-hub-api';

/**
 * 並べる区分ごとの札の並び（上から順）。
 * @graph-connects none
 */
export type Arrangement = ReadonlyMap<ProductBacklogSectionKind, readonly ProductBacklogCard[]>;

/**
 * 動かした札1枚（rank は区分の中で何番目か）。
 * @graph-connects none
 */
export interface PendingMove {
  card: ProductBacklogCard;
  from: { kind: ProductBacklogSectionKind; rank: number };
  to: { kind: ProductBacklogSectionKind; rank: number };
}

/**
 * 一覧の応答から、並べる区分の元の並びを作る。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件一覧の応答から、並べる区分（置き場所が決まっていない案件を含む）の元の札の並びを作る
 * @graph-connects none
 */
export function baseArrangement(sections: readonly ProductBacklogSection[]): Arrangement {
  return new Map(sections.filter((s) => s.arrangeable).map((s) => [s.kind, s.cards]));
}

/**
 * 並びの中の値（キー。キーの無い行は null）。
 * @graph-connects none
 */
function tokensOf(cards: readonly ProductBacklogCard[]): (string | null)[] {
  return cards.map((card) => card.key);
}

/**
 * 保存されている下書きを、今の一覧の並びに当てる。下書きを作ったときの並びが今の並びと違えば
 * （未振り分けなら、置いた札がもう未振り分けに無ければ）当てずに、変わった案件のキーを返す。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 保存された並べ替えの下書きを今のスプシの並びに当て、作ったときから並びが変わっていれば当てずに変わった案件を返す
 * @graph-connects tokensOf [calls] 並びの中の値
 */
export function overlayDraft(
  base: Arrangement,
  draft: ProductBacklogDraft,
): { status: 'ok'; arrangement: Arrangement } | { status: 'stale'; changed: string[] } {
  const byKey = new Map<string, ProductBacklogCard>();
  for (const cards of base.values()) {
    for (const card of cards) if (card.key !== null) byKey.set(card.key, card);
  }
  const next = new Map(base);
  for (const section of draft.sections) {
    const current = base.get(section.kind) ?? [];
    const now = tokensOf(current);
    if (section.kind === 'unassigned') {
      const placed = section.base.filter((token) => !section.target.includes(token));
      const gone = placed.filter(
        (token): token is string => token !== null && !now.includes(token),
      );
      if (gone.length > 0) return { status: 'stale', changed: gone };
      next.set(
        section.kind,
        current.filter((card) => !placed.includes(card.key)),
      );
      continue;
    }
    const changed = new Set<string>();
    for (let i = 0; i < Math.max(section.base.length, now.length); i += 1) {
      const before = section.base[i] ?? null;
      const after = now[i] ?? null;
      if (before === after) continue;
      if (before !== null) changed.add(before);
      if (after !== null) changed.add(after);
    }
    if (changed.size > 0 || now.length !== section.base.length) {
      return { status: 'stale', changed: [...changed] };
    }
    const keyless = current.filter((card) => card.key === null);
    next.set(
      section.kind,
      section.target.flatMap((token) => {
        const card = token === null ? keyless.shift() : byKey.get(token);
        return card === undefined ? [] : [card];
      }),
    );
  }
  return { status: 'ok', arrangement: next };
}

/**
 * 札を区分の index 番目（無ければ末尾）へ動かした並びを返す。動かせない札・動かせない先なら null。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件の札を同じ区分の別の位置か付け替えられる区分の指定の位置（指定が無ければ末尾）へ動かした並びを作り、動かせない札や先なら作らない
 * @graph-connects canMoveCard [calls] 動かせる先の表
 */
export function moveCard(
  arrangement: Arrangement,
  rowNumber: number,
  to: ProductBacklogSectionKind,
  index: number | null,
): Arrangement | null {
  const found = [...arrangement].flatMap(([kind, cards]) => {
    const card = cards.find((c) => c.rowNumber === rowNumber);
    return card === undefined ? [] : [{ kind, cards, card }];
  })[0];
  if (found === undefined || found.card.lockReason !== null || !canMoveCard(found.kind, to)) {
    return null;
  }
  const { kind: from, cards, card: moving } = found;
  const next = new Map(arrangement);
  const without = cards.filter((c) => c.rowNumber !== rowNumber);
  next.set(from, without);
  const target = [...(from === to ? without : (arrangement.get(to) ?? []))];
  const position = index === null ? target.length : Math.max(0, Math.min(index, target.length));
  target.splice(position, 0, moving);
  next.set(to, target);
  return next;
}

/**
 * 並びと元の並びの差を、保存する下書きの区分にする（並びが変わった区分だけ）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 画面で動かした並びと元の並びの差から、API に保存する下書きの区分（区切りの文言・元の並び・動かした後の並び）を作る
 * @graph-connects tokensOf [calls] 並びの中の値
 */
export function draftSectionsOf(
  sections: readonly ProductBacklogSection[],
  arrangement: Arrangement,
): ProductBacklogDraftSection[] {
  return sections.flatMap((section) => {
    const current = arrangement.get(section.kind);
    if (!section.arrangeable || current === undefined) return [];
    const base = tokensOf(section.cards);
    const target = tokensOf(current);
    if (base.length === target.length && base.every((token, i) => token === target[i])) return [];
    return [{ kind: section.kind, label: section.label, base, target }];
  });
}

/**
 * 動かした札（下の帯の「中身を見る」と札の印に使う）。API と同じ数え方で、動かさずに済む札は数えない。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 画面で動かした並びと元の並びから、動かした札とその前後の区分・順位を、API と同じ数え方で決める
 * @graph-connects movedCards [calls] 動かした札
 */
export function pendingMoves(base: Arrangement, arrangement: Arrangement): PendingMove[] {
  const ids = (arr: Arrangement) =>
    new Map([...arr].map(([kind, cards]) => [kind, cards.map((c) => c.rowNumber)]));
  const pinned = new Set<number>();
  const cardOf = new Map<number, ProductBacklogCard>();
  for (const cards of base.values()) {
    for (const card of cards) {
      cardOf.set(card.rowNumber, card);
      if (card.lockReason !== null) pinned.add(card.rowNumber);
    }
  }
  const rankIn = (arr: Arrangement, kind: ProductBacklogSectionKind, rowNumber: number) =>
    (arr.get(kind) ?? []).findIndex((c) => c.rowNumber === rowNumber) + 1;
  return movedCards(ids(base), ids(arrangement), pinned).flatMap((move) => {
    const card = cardOf.get(move.id);
    if (card === undefined) return [];
    return [
      {
        card,
        from: { kind: move.from, rank: rankIn(base, move.from, move.id) },
        to: { kind: move.to, rank: rankIn(arrangement, move.to, move.id) },
      },
    ];
  });
}

/**
 * 並びを、一覧の区分に戻す（順位は並びの順で振り直す）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 画面で動かした並びを、一覧に出す区分の札の並びに戻し、区分の中の順位を上から振り直す
 * @graph-connects none
 */
export function arrangedSections(
  sections: readonly ProductBacklogSection[],
  arrangement: Arrangement,
): ProductBacklogSection[] {
  return sections.map((section) => {
    const cards = arrangement.get(section.kind);
    if (!section.arrangeable || cards === undefined) return section;
    return { ...section, cards: cards.map((card, index) => ({ ...card, rank: index + 1 })) };
  });
}
